import { useEffect, useRef, useState } from "react";
import { FolderPlus, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function SaveTripDialog({ offer, stay, onClose }) {
  const [trips, setTrips] = useState(null);
  const [mode, setMode] = useState("new");
  const [tripId, setTripId] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const interacted = useRef(false);

  useEffect(() => {
    api.get("/trips").then(({ data }) => {
      setTrips(data);
      if (data.length > 0) {
        setTripId((cur) => cur || data[0].id);
        if (!interacted.current) setMode("existing");
      }
    }).catch(() => setTrips([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const base = stay ? { stay } : { offer };
      const body = mode === "existing" ? { ...base, trip_id: tripId } : { ...base, new_trip_name: name.trim() || undefined };
      const { data } = await api.post(stay ? "/planning/stays/save" : "/planning/offers/save", body);
      toast.success(data.created_trip ? "Saved to a new trip — planned, not booked" : "Saved to trip — planned, not booked");
      onClose(true);
    } catch {
      toast.error(stay ? "Could not save the hotel" : "Could not save the offer");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-surface border border-sand-300 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="save-trip-dialog">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="label-tag mb-1">Save to trip</p>
            <p className="text-sm text-sand-600">Adds planned (not booked) flight reservations.</p>
          </div>
          <button onClick={() => onClose(false)} className="text-sand-400 hover:text-sand-900" data-testid="save-trip-close"><X size={16} /></button>
        </div>
        {trips === null ? (
          <p className="text-sm text-sand-500 py-4">Loading trips…</p>
        ) : (
          <div className="space-y-3">
            {trips.length > 0 && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="radio" checked={mode === "existing"} onChange={() => { interacted.current = true; setMode("existing"); }} className="mt-1 accent-ember" data-testid="save-trip-mode-existing" />
                <span className="flex-1">
                  <span className="block text-sm font-medium mb-1.5">Existing trip</span>
                  <select value={tripId} onChange={(e) => { interacted.current = true; setTripId(e.target.value); setMode("existing"); }}
                    className="w-full border border-sand-300 bg-surface px-3 py-2 text-sm focus:outline-none focus:border-ember"
                    data-testid="save-trip-select">
                    {trips.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.start_date ? ` · ${t.start_date}` : ""}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
            )}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="radio" checked={mode === "new"} onChange={() => { interacted.current = true; setMode("new"); }} className="mt-1 accent-ember" data-testid="save-trip-mode-new" />
              <span className="flex-1">
                <span className="block text-sm font-medium mb-1.5">New trip</span>
                <input value={name} onChange={(e) => { interacted.current = true; setName(e.target.value); setMode("new"); }}
                  placeholder="Trip name (optional)"
                  className="w-full border border-sand-300 bg-surface px-3 py-2 text-sm focus:outline-none focus:border-ember"
                  data-testid="save-trip-name-input" />
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => onClose(false)} className="btn-swiss btn-ghost text-sm">Cancel</button>
              <button onClick={save} disabled={saving || trips === null || (mode === "existing" && !tripId)}
                className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="save-trip-confirm">
                {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FolderPlus size={14} className="mr-1.5" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
